import pubSubService from './PubSubService'
import { EVENT_STRATEGIES } from './eventStrategies'

/**
 * PubSub Event Handler
 * Handles events from the PubSub service and updates Redux state accordingly
 */
class PubSubEventHandler {
    constructor() {
        this.eventListeners = []
        this.isInitialized = false
        this.dispatch = null // Will be set during initialization
    }

    /**
     * Initialize the event handler
     * This should be called once when the app starts
     * @param {Function} dispatch - Redux dispatch function
     */
    initialize(dispatch) {
        if (this.isInitialized) {
            return
        }

        // Store the dispatch function for later use
        this.dispatch = dispatch

        // Register event listeners for different event types
        this._registerEventListeners()
        this.isInitialized = true
    }

    /**
     * Register event listeners for different event types
     * @private
     */
    _registerEventListeners() {
        // Listen for all events and dispatch to specific handlers
        this.removeAllEventListener = pubSubService.addEventListener('*', data => {
            this._handleEvent(data)
        })
    }

    /**
     * Handle an event from the PubSub service using strategy pattern
     * @param {Object} data - The event data
     * @private
     */
    _handleEvent(res) {
        const { type, payload } = res.data || {}

        // console.log(`Received `, JSON.stringify(res.data))
        // console.log(`Received PubSub event: ${type}`, payload)

        // Use strategy pattern instead of switch case
        const strategy = EVENT_STRATEGIES[type]
        if (strategy) {
            strategy.handle(payload, this.dispatch, this._notifyEventListeners.bind(this))
        } else {
            // Fallback for unknown events - just notify listeners
            this._notifyEventListeners(type, payload)
        }
    }

    /**
     * Notify custom event listeners
     * @param {string} type - The event type
     * @param {Object} payload - The event payload
     * @private
     */
    _notifyEventListeners(type, payload) {
        this.eventListeners.forEach(listener => {
            if (listener.type === type || listener.type === '*') {
                listener.callback(payload)
            }
        })
    }

    /**
     * Add a custom event listener
     * @param {string} type - The event type to listen for, or '*' for all events
     * @param {Function} callback - The callback function to be called when the event is received
     * @returns {Function} A function to remove the listener
     */
    addEventListener(type, callback) {
        const listener = { type, callback }
        this.eventListeners.push(listener)

        // Return a function to remove the listener
        return () => {
            this.eventListeners = this.eventListeners.filter(l => l !== listener)
        }
    }

    /**
     * Clean up all event listeners
     * This should be called when the app is shutting down
     */
    cleanup() {
        if (this.removeAllEventListener) {
            this.removeAllEventListener()
        }

        this.eventListeners = []
        this.isInitialized = false
        this.dispatch = null
    }
}

// Create a singleton instance
const pubSubEventHandler = new PubSubEventHandler()

export default pubSubEventHandler
